import { useTranslation } from '../../i18n/useTranslation'
import { useTheme } from '../../hooks/useTheme'
import { Button } from '../ui'

interface TrainingSetActionsProps {
  canComplete: boolean
  isCompleting: boolean
  isDeleting: boolean
  isPaused: boolean
  isPausing: boolean
  isResuming: boolean
  isCancelingPause: boolean
  isSendingReport: boolean
  isUnpaid: boolean
  isUpdatingUnpaid: boolean
  onComplete: () => void
  onDelete: () => void
  onPause: () => void
  onResume: () => void
  onCancelPause: () => void
  onGetReport: () => void
  onToggleUnpaid: () => void
}

const tileClass =
  'flex-1 min-w-0 flex flex-col items-center justify-center gap-1 px-1 py-3 rounded-xl transition-opacity disabled:opacity-40'

const labelClass = 'text-[10px] font-medium text-center leading-tight'

export function TrainingSetActions({
  canComplete,
  isCompleting,
  isDeleting,
  isPaused,
  isPausing,
  isResuming,
  isCancelingPause,
  isSendingReport,
  isUnpaid,
  isUpdatingUnpaid,
  onComplete,
  onDelete,
  onPause,
  onResume,
  onCancelPause,
  onGetReport,
  onToggleUnpaid,
}: TrainingSetActionsProps) {
  const { t } = useTranslation()
  const theme = useTheme()

  const accent = theme.accent.text
  const tileBase: React.CSSProperties = {
    backgroundColor: theme.cardBg,
    border: `1px solid ${theme.cardBorder}`,
  }
  const tileActive: React.CSSProperties = {
    backgroundColor: `${theme.buttonColor}12`,
    border: `1px solid ${theme.buttonColor}25`,
  }

  return (
    <section className="mb-5">
      <Button
        type="button"
        variant={canComplete ? 'primary' : 'secondary'}
        size="lg"
        fullWidth
        disabled={!canComplete || isCompleting}
        onClick={onComplete}
        className="mb-3"
        data-tour="tour-set-action-complete"
      >
        <span className="flex items-center justify-center gap-2">
          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
            <polyline points="22 4 12 14.01 9 11.01"/>
          </svg>
          {isCompleting ? t.trainingSetActions.completing : t.trainingSetActions.complete}
        </span>
      </Button>

      <div className="flex gap-1.5">
        <button
          type="button"
          onClick={onGetReport}
          disabled={isSendingReport}
          className={tileClass}
          style={tileBase}
          data-tour="tour-set-action-report"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: accent }}>
            <line x1="18" y1="20" x2="18" y2="10"/>
            <line x1="12" y1="20" x2="12" y2="4"/>
            <line x1="6" y1="20" x2="6" y2="14"/>
          </svg>
          <span className={labelClass} style={{ color: accent }}>
            {t.trainingSetActions.report}
          </span>
        </button>

        <button
          type="button"
          onClick={onToggleUnpaid}
          disabled={isUpdatingUnpaid}
          className={tileClass}
          style={isUnpaid ? tileActive : tileBase}
          data-tour="tour-set-action-unpaid"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: accent }}>
            <rect x="2" y="6" width="20" height="12" rx="2"/>
            <circle cx="12" cy="12" r="3"/>
            <path d="M6 12h.01M18 12h.01"/>
          </svg>
          <span className={labelClass} style={{ color: accent }}>
            {isUpdatingUnpaid
              ? t.trainingSetActions.updatingUnpaid
              : isUnpaid
                ? t.trainingSetActions.clearUnpaid
                : t.trainingSetActions.markUnpaid}
          </span>
        </button>

        {isPaused ? (
          <>
            <button
              type="button"
              onClick={onResume}
              disabled={isResuming || isCancelingPause}
              className={tileClass}
              style={tileBase}
              data-tour="tour-set-action-resume"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: accent }}>
                <polygon points="5 3 19 12 5 21 5 3"/>
              </svg>
              <span className={labelClass} style={{ color: accent }}>
                {isResuming ? t.trainingSetActions.resuming : t.trainingSetActions.resume}
              </span>
            </button>

            <button
              type="button"
              onClick={onCancelPause}
              disabled={isResuming || isCancelingPause}
              className={tileClass}
              style={tileBase}
              data-tour="tour-set-action-cancel-pause"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: accent }}>
                <circle cx="12" cy="12" r="10"/>
                <line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/>
              </svg>
              <span className={labelClass} style={{ color: accent }}>
                {isCancelingPause ? t.trainingSetActions.cancelingPause : t.trainingSetActions.cancelPause}
              </span>
            </button>
          </>
        ) : (
          <button
            type="button"
            onClick={onPause}
            disabled={isPausing}
            className={tileClass}
            style={tileBase}
            data-tour="tour-set-action-pause"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: accent }}>
              <rect x="6" y="4" width="4" height="16"/><rect x="14" y="4" width="4" height="16"/>
            </svg>
            <span className={labelClass} style={{ color: accent }}>
              {isPausing ? t.trainingSetActions.pausing : t.trainingSetActions.pause}
            </span>
          </button>
        )}

        <button
          type="button"
          onClick={onDelete}
          disabled={isDeleting}
          className={tileClass}
          style={tileBase}
          data-tour="tour-set-action-delete"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: accent }}>
            <polyline points="3 6 5 6 21 6"/>
            <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
            <path d="M10 11v6"/><path d="M14 11v6"/>
            <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
          </svg>
          <span className={labelClass} style={{ color: accent }}>
            {isDeleting ? t.trainingSetActions.deleting : t.trainingSetActions.delete}
          </span>
        </button>
      </div>
    </section>
  )
}
